import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

function PhotoLightbox({ photos, initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const touchStartX = useRef(null);
  
  const handlePrev = () => setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  const handleNext = () => setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    // Swipe threshold of 50px
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext(); // Swipe left = next
      } else {
        handlePrev(); // Swipe right = prev
      }
    }
    touchStartX.current = null;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={onClose}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors bg-black/50 border-none cursor-pointer z-10 p-2">
          <X size={28} />
        </button>
        
        {/* Photo counter */}
        {photos.length > 1 && (
          <div className="absolute top-4 left-4 text-white/70 font-mono text-sm bg-black/50 px-3 py-1">
            {currentIndex + 1} / {photos.length}
          </div>
        )}
        
        {/* Navigation arrows - larger touch targets */}
        {photos.length > 1 && (
          <>
            <button 
              onClick={(e) => { e.stopPropagation(); handlePrev(); }} 
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors bg-black/60 hover:bg-black/80 p-3 sm:p-4 border-none cursor-pointer z-10"
            >
              <ChevronLeft size={32} />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); handleNext(); }} 
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors bg-black/60 hover:bg-black/80 p-3 sm:p-4 border-none cursor-pointer z-10"
            >
              <ChevronRight size={32} />
            </button>
          </>
        )}
        
        {/* Main image with animation */}
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          src={photos[currentIndex]}
          alt={`Photo ${currentIndex + 1}`}
          className="max-w-[90vw] max-h-[80vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
        
        {/* Dot indicators */}
        {photos.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                className={`w-2.5 h-2.5 rounded-full transition-colors border-none cursor-pointer ${i === currentIndex ? 'bg-white' : 'bg-white/30 hover:bg-white/50'}`}
              />
            ))}
          </div>
        )}
        
        {/* Swipe hint for mobile */}
        {photos.length > 1 && (
          <p className="absolute bottom-16 left-1/2 -translate-x-1/2 text-white/40 text-xs font-body sm:hidden">
            Swipe to navigate
          </p>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export { PhotoLightbox };
